<template>
    <div
        :style="{ width: width + 'px', height: height + 'px' }"
        class="rounded-full flex items-center justify-center bg-gray-200 text-gray-700 font-bold"
    >
        <img v-if="fetchStatus === 'success' && !isRefreshing" :src="imageUrl" :alt="name" class="w-full h-full rounded-full" />
        <div class="no-avatar" v-else-if="fetchStatus === 'error' && !isRefreshing">
            <span>{{ initials }}</span>
        </div>
        <div v-else class="loading-image">
            <svg class="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

const props = defineProps({
    name: {
        type: String,
        required: false,
        default: 'Anonymous',
    },
    person_id: {
        type: Number,
        required: true,
    },
    height: {
        type: Number,
        default: 40,
    },
    width: {
        type: Number,
        default: 40,
    },
});

const initials = props.name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);

const fetchStatus = ref<'loading' | 'success' | 'error'>('loading');
const runtimeConfig = useRuntimeConfig();
const { avatarRefreshKey, isRefreshing } = useAvatarRefresh();

// Add cache-busting parameter when avatar refresh key changes
const imageUrl = computed(() => {
    const baseUrl = `${runtimeConfig.public.pb.pbCDN}/avatars/people/${props.person_id}.png`;
    return avatarRefreshKey.value > 0 ? `${baseUrl}?v=${avatarRefreshKey.value}` : baseUrl;
});

const loadImage = () => {
    fetchStatus.value = 'loading';
    const img = new Image();
    img.src = imageUrl.value;

    img.onload = () => {
        fetchStatus.value = 'success';
    };

    img.onerror = () => {
        fetchStatus.value = 'error';
    };
};

loadImage();

watch(() => props.person_id, () => {
    loadImage();
});

// Watch for avatar refresh and reload image
watch(avatarRefreshKey, () => {
    // Set to loading immediately when refresh is triggered
    fetchStatus.value = 'loading';
});

// Watch for when refresh period ends and reload the image
watch(isRefreshing, (newValue) => {
    if (!newValue) {
        // Refresh period ended, load the new image
        loadImage();
    }
});
</script>

<style lang="scss" scoped>
.no-avatar {
    color: #17b26a;
    text-align: center;
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 14px; /* 100% */
    border-radius: 50%;
    border: 1px solid #17b26a;
    background: rgba(23, 178, 106, 0.1);
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    aspect-ratio: 1 / 1;
}

// loading image skeleton
.loading-image {
    @apply bg-gray-100 rounded-full flex items-center justify-center;
    width: 100%;
    height: 100%;
    
    .loading-spinner {
        @apply text-gray-400;
        width: 40%;
        height: 40%;
        animation: spin 1s linear infinite;
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
